import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, Globe, Phone, Mail, ExternalLink, Search, 
  PawPrint, CheckCircle, Clock, Users, ArrowLeft, Loader2 
} from 'lucide-react';
import PartnerRegistrationDialog from '@/components/PartnerRegistrationDialog';

const PartnersPage = ({ user }) => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    totalLookups: 0
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get('/public/partners');
      setPartners(response.data.partners || []);
      setStats({
        total: response.data.total || 0,
        active: response.data.active || 0,
        totalLookups: response.data.total_lookups || 0
      });
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPartners = partners.filter(partner => 
    partner.registry_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    partner.country?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <PawPrint className="w-8 h-8 text-primary" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Partner Network
              </span>
            </div>
          </div>
          <Button onClick={() => navigate('/auth')} className="bg-primary hover:bg-primary/90 rounded-full">
            {user ? 'Dashboard' : 'Sign In'}
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-primary text-white py-16">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Connected Partner Registries
          </h1>
          <p className="text-xl max-w-2xl mx-auto mb-8 opacity-90">
            Our federated network connects multiple microchip databases worldwide, 
            ensuring pets can always be found.
          </p>
          
          {/* Stats */}
          <div className="flex justify-center gap-8 flex-wrap">
            <div className="bg-white/10 rounded-xl px-6 py-4">
              <div className="text-3xl font-bold">{stats.active}</div>
              <div className="text-sm opacity-80">Active Partners</div>
            </div>
            <div className="bg-white/10 rounded-xl px-6 py-4">
              <div className="text-3xl font-bold">{stats.total}</div>
              <div className="text-sm opacity-80">Total Registries</div>
            </div>
            <div className="bg-white/10 rounded-xl px-6 py-4">
              <div className="text-3xl font-bold">{(stats.totalLookups || 0).toLocaleString()}</div>
              <div className="text-sm opacity-80">Searches Performed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Search & Partners List */}
      <section className="container mx-auto px-6 py-12">
        {/* Search Bar */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              type="text"
              placeholder="Search partners by name or country..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 rounded-full"
            />
          </div>
        </div>

        {/* Partners Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredPartners.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-600 mb-2">No Partners Found</h3>
            <p className="text-stone-500 mb-6">
              {searchQuery ? 'Try a different search term' : 'Be the first to join our network!'}
            </p>
            <Button onClick={() => setShowPartnerDialog(true)} className="bg-primary">
              Become a Partner
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPartners.map((partner) => (
              <Card key={partner.partner_id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-start gap-4">
                    {partner.logo_url ? (
                      <img 
                        src={partner.logo_url} 
                        alt={partner.registry_name}
                        className="w-16 h-16 rounded-lg object-cover bg-stone-100"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-16 h-16 rounded-lg bg-primary/10 items-center justify-center hidden">
                      <Building2 className="w-8 h-8 text-primary" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">{partner.registry_name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={partner.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                          {partner.status === 'active' ? (
                            <><CheckCircle className="w-3 h-3 mr-1" /> Active</>
                          ) : (
                            <><Clock className="w-3 h-3 mr-1" /> Pending</>
                          )}
                        </Badge>
                        {partner.country && (
                          <span className="text-xs text-stone-500">{partner.country}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {partner.description && (
                    <p className="text-sm text-stone-600 line-clamp-2">{partner.description}</p>
                  )}
                  
                  <div className="space-y-2 text-sm">
                    {partner.website_url && (
                      <a 
                        href={partner.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-primary hover:underline"
                      >
                        <Globe className="w-4 h-4" />
                        Visit Website
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {partner.contact_phone && (
                      <a 
                        href={`tel:${partner.contact_phone}`}
                        className="flex items-center gap-2 text-stone-600 hover:text-primary"
                      >
                        <Phone className="w-4 h-4" />
                        {partner.contact_phone}
                      </a>
                    )}
                    {partner.contact_email && (
                      <a 
                        href={`mailto:${partner.contact_email}`}
                        className="flex items-center gap-2 text-stone-600 hover:text-primary"
                      >
                        <Mail className="w-4 h-4" />
                        {partner.contact_email}
                      </a>
                    )}
                  </div>

                  {partner.total_lookups > 0 && (
                    <div className="pt-2 border-t">
                      <span className="text-xs text-stone-500">
                        {partner.total_lookups.toLocaleString()} searches performed
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="bg-stone-100 py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Want to Join Our Network?
          </h2>
          <p className="text-stone-600 mb-8 max-w-2xl mx-auto">
            Connect your microchip registry to our federated search network for just $20/month. 
            Get instant access to millions of pet owners searching for their lost companions.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              onClick={() => setShowPartnerDialog(true)} 
              className="bg-primary hover:bg-primary/90 h-12 px-8 rounded-full"
            >
              <Building2 className="w-5 h-5 mr-2" />
              Become a Partner
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/search')}
              className="h-12 px-8 rounded-full"
            >
              <Search className="w-5 h-5 mr-2" />
              Search Microchips
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-300 py-8">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <PawPrint className="w-6 h-6" />
            <span className="text-lg font-bold">International Pet Registry</span>
          </div>
          <p className="text-sm">© 2025 International Pet Registry. All rights reserved.</p>
        </div>
      </footer>

      {/* Partner Registration Dialog */}
      <PartnerRegistrationDialog 
        open={showPartnerDialog} 
        onOpenChange={setShowPartnerDialog} 
      />
    </div>
  );
};

export default PartnersPage;
